import { useRef, useState, useEffect } from "react";
import html2canvas from "html2canvas";
import type { Sheet } from "@yamber/rules-engine";
import { getTemplate } from "@yamber/rules-engine";
import { Confetti } from "./Confetti";

interface GameSummaryProps {
  templateId: string;
  sheet: Sheet;
  totalScore: number;
  playerName?: string;
  onClose: () => void;
  onNewGame: () => void;
}

export function GameSummary({
  templateId,
  sheet,
  totalScore,
  playerName = "Player",
  onClose,
  onNewGame,
}: GameSummaryProps) {
  const summaryRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [shareMessage, setShareMessage] = useState<string | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);

  const template = getTemplate(templateId);

  // Trigger confetti celebration on mount
  useEffect(() => {
    setShowConfetti(true);
  }, []);

  // Generate shareable text summary
  const generateShareText = () => {
    const templateName = template?.name || templateId;
    return `I scored ${totalScore} points in ${templateName}! Can you beat my score? #YambPro #DiceGame`;
  };

  // Export as image
  const handleExportImage = async () => {
    if (!summaryRef.current) return;

    setIsExporting(true);
    try {
      const canvas = await html2canvas(summaryRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        logging: false,
        useCORS: true,
      });

      // Create download link
      const link = document.createElement("a");
      link.download = `yambpro-score-${totalScore}-${Date.now()}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();

      setShareMessage("Image downloaded!");
      setTimeout(() => setShareMessage(null), 3000);
    } catch (error) {
      console.error("Failed to export image:", error);
      setShareMessage("Failed to export image");
    } finally {
      setIsExporting(false);
    }
  };

  // Copy share text to clipboard
  const handleCopyText = async () => {
    const text = generateShareText();
    try {
      await navigator.clipboard.writeText(text);
      setShareMessage("Copied to clipboard!");
      setTimeout(() => setShareMessage(null), 3000);
    } catch (error) {
      console.error("Failed to copy:", error);
      setShareMessage("Failed to copy");
    }
  };

  // Share via Web Share API (if available)
  const handleNativeShare = async () => {
    if (!navigator.share) {
      handleCopyText();
      return;
    }

    try {
      await navigator.share({
        title: "My YambPro Score",
        text: generateShareText(),
        url: window.location.origin,
      });
    } catch (error) {
      // User cancelled or share failed
      if ((error as Error).name !== "AbortError") {
        console.error("Share failed:", error);
      }
    }
  };

  // Calculate column totals
  const getColumnTotal = (columnId: string) => {
    const column = sheet.columns[columnId];
    if (!column) return 0;
    return Object.values(column.cells).reduce(
      (sum, cell) => sum + (cell.score ?? 0),
      0
    );
  };

  return (
    <div className="game-summary-overlay">
      <Confetti active={showConfetti} pieceCount={60} duration={5000} />
      <div className="game-summary-modal">
        {/* Exportable Summary Card */}
        <div ref={summaryRef} className="game-summary-card">
          <div className="summary-header">
            <div className="summary-logo">YambPro</div>
            <div className="summary-date">
              {new Date().toLocaleDateString()}
            </div>
          </div>

          <div className="summary-score-section">
            <div className="summary-player">{playerName}</div>
            <div className="summary-total-score">{totalScore}</div>
            <div className="summary-label">FINAL SCORE</div>
          </div>

          <div className="summary-template">
            {template?.name || templateId}
          </div>

          <div className="summary-breakdown">
            <div className="breakdown-header">Score Breakdown</div>
            <div className="breakdown-columns">
              {Object.entries(sheet.columns).map(([colId, column]) => (
                <div key={colId} className="breakdown-column">
                  <span className="column-name">{column.type}</span>
                  <span className="column-score">{getColumnTotal(colId)}</span>
                </div>
              ))}
            </div>
            {sheet.upperBonus > 0 && (
              <div className="breakdown-bonus">
                <span>Upper Bonus</span>
                <span>+{sheet.upperBonus}</span>
              </div>
            )}
          </div>

          <div className="summary-footer">
            <span>Play at YambPro.com</span>
          </div>
        </div>

        {/* Share Actions */}
        <div className="summary-actions">
          {shareMessage && (
            <div className="share-message">{shareMessage}</div>
          )}

          <div className="share-buttons">
            <button
              type="button"
              className="share-btn share-btn--image"
              onClick={handleExportImage}
              disabled={isExporting}
            >
              {isExporting ? "Exporting..." : "Save Image"}
            </button>

            <button
              type="button"
              className="share-btn share-btn--copy"
              onClick={handleCopyText}
            >
              Copy Text
            </button>

            {"share" in navigator && (
              <button
                type="button"
                className="share-btn share-btn--share"
                onClick={handleNativeShare}
              >
                Share
              </button>
            )}
          </div>

          <div className="summary-nav-buttons">
            <button
              type="button"
              className="nav-btn nav-btn--secondary"
              onClick={onClose}
            >
              View Scoresheet
            </button>
            <button
              type="button"
              className="nav-btn nav-btn--primary"
              onClick={onNewGame}
            >
              Play Again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
